import React, { useState } from 'react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ExportOptions = ({ reportType, reportData, filters, onClose }) => {
  const [exportFormat, setExportFormat] = useState('csv');
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      if (exportFormat === 'csv') {
        exportToCSV();
      } else if (exportFormat === 'pdf') {
        exportToPDF();
      } else if (exportFormat === 'json') {
        exportToJSON();
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export report');
    } finally {
      setExporting(false);
      onClose();
    }
  };

  const exportToCSV = () => {
    let csvContent = '';
    let headers = [];
    let rows = [];

    // Define headers based on report type
    switch (reportType) {
      case 'sales':
        headers = ['Date', 'Transaction ID', 'Amount', 'Payment Method', 'Status'];
        rows = reportData?.map(row => [
          format(new Date(row?.transaction_date), 'yyyy-MM-dd'),
          row?.id,
          parseFloat(row?.total_amount || 0)?.toFixed(2),
          row?.payment_method,
          row?.payment_status
        ]);
        break;
      case 'attendance':
        headers = ['Date', 'Member', 'Check-in', 'Check-out', 'Duration (min)'];
        rows = reportData?.map(row => [
          format(new Date(row?.check_in_time), 'yyyy-MM-dd'),
          row?.user_profiles?.full_name || '',
          format(new Date(row?.check_in_time), 'HH:mm'),
          row?.check_out_time ? format(new Date(row?.check_out_time), 'HH:mm') : '',
          row?.duration_minutes || ''
        ]);
        break;
      case 'revenue':
        headers = ['Invoice #', 'Date', 'Amount', 'Status'];
        rows = reportData?.map(row => [
          row?.invoice_number || '',
          format(new Date(row?.billing_date), 'yyyy-MM-dd'),
          parseFloat(row?.total_amount || 0)?.toFixed(2),
          row?.payment_status
        ]);
        break;
      case 'membership':
        headers = ['Member', 'Plan', 'Price', 'Status', 'Start Date'];
        rows = reportData?.map(row => [
          row?.user_profiles?.full_name || '',
          row?.membership_plans?.plan_name || '',
          parseFloat(row?.final_price || 0)?.toFixed(2),
          row?.status,
          format(new Date(row?.start_date), 'yyyy-MM-dd')
        ]);
        break;
      case 'reconciliation':
        headers = ['Date', 'Transaction ID', 'Expected Amount', 'Actual Amount', 'Difference', 'Status'];
        rows = reportData?.map(row => [
          format(new Date(row?.transaction_date), 'yyyy-MM-dd'),
          row?.transaction_id,
          parseFloat(row?.expected_amount || 0)?.toFixed(2),
          parseFloat(row?.actual_amount || 0)?.toFixed(2),
          parseFloat(row?.amount_difference || 0)?.toFixed(2),
          row?.reconciliation_status
        ]);
        break;
      default:
        break;
    }

    // Build CSV content
    csvContent = headers?.join(',') + '\n';
    rows?.forEach(row => {
      csvContent += row?.map(cell => `"${cell}"`)?.join(',') + '\n';
    });

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link?.setAttribute('href', url);
    link?.setAttribute('download', `${reportType}_report_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  const exportToPDF = () => {
    alert('PDF export functionality would require jsPDF library integration');
  };

  const exportToJSON = () => {
    const jsonContent = JSON.stringify(reportData, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link?.setAttribute('href', url);
    link?.setAttribute('download', `${reportType}_report_${format(new Date(), 'yyyy-MM-dd')}.json`);
    link.style.visibility = 'hidden';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-foreground flex items-center gap-2">
            <Icon name="Download" size={24} />
            Export Report
          </h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="space-y-4 mb-6">
          <div>
            <label className="text-sm font-medium text-foreground mb-3 block">Export Format</label>
            <div className="space-y-2">
              {[
                { id: 'csv', label: 'CSV (Excel Compatible)', icon: 'FileSpreadsheet', description: 'Best for data analysis' },
                { id: 'pdf', label: 'PDF Document', icon: 'FileText', description: 'Best for printing and sharing' },
                { id: 'json', label: 'JSON Data', icon: 'Code', description: 'Best for developers' }
              ]?.map(format => (
                <button
                  key={format?.id}
                  onClick={() => setExportFormat(format?.id)}
                  className={`w-full text-left p-4 rounded-lg border transition-colors ${
                    exportFormat === format?.id
                      ? 'border-primary bg-primary/10' :'border-border hover:bg-muted'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Icon name={format?.icon} size={20} className={exportFormat === format?.id ? 'text-primary' : 'text-muted-foreground'} />
                    <div className="flex-1">
                      <p className="font-medium text-foreground mb-1">{format?.label}</p>
                      <p className="text-xs text-muted-foreground">{format?.description}</p>
                    </div>
                    {exportFormat === format?.id && (
                      <Icon name="CheckCircle" size={20} className="text-primary" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="p-4 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground mb-2">Report Summary:</p>
            <ul className="text-sm text-foreground space-y-1">
              <li>• Report Type: <span className="font-medium capitalize">{reportType}</span></li>
              <li>• Total Records: <span className="font-medium">{reportData?.length}</span></li>
              <li>• Date Range: <span className="font-medium">{filters?.dateRange} days</span></li>
            </ul>
          </div>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={exporting} className="flex-1">
            {exporting ? (
              <>
                <Icon name="Loader2" size={18} className="animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Icon name="Download" size={18} />
                Export
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExportOptions;